/** Class the assign callout carries while its assignment is being saved; TextEditor styles it. */
export const CALLOUT_PENDING_CLASS = 'is-pending'
export const CALLOUT_SPINNER_CLASS = 'callout-spinner'

/**
 * The spinner is a real child: the callout's ::before/::after already draw its arrow. ProseMirror ignores DOM changes
 * inside a leaf node, so the editor does not react to it.
 */
export function markCalloutPending(element: HTMLElement | null | undefined, pending: boolean): void {
  if (!element) return
  element.classList.toggle(CALLOUT_PENDING_CLASS, pending)
  const spinner = element.querySelector(`.${CALLOUT_SPINNER_CLASS}`)
  if (pending) {
    element.setAttribute('aria-busy', 'true')
    if (!spinner) {
      const created = document.createElement('span')
      created.className = CALLOUT_SPINNER_CLASS
      created.setAttribute('aria-hidden', 'true')
      element.appendChild(created)
    }
  } else {
    element.removeAttribute('aria-busy')
    spinner?.remove()
  }
}

/**
 * Marks the clicked callout and every callout of the same variable in its editor until the returned function runs.
 * ProseMirror re-renders callout nodes while the save is in flight (focus moves to the menu, the variable store
 * refetches), which would drop a mark set once, so the marks are reapplied whenever the editor's DOM changes.
 * `editorRoot` defaults to the editor around the clicked callout.
 */
export function watchPendingCallouts(
  anchor: HTMLElement | null | undefined,
  variableName: string,
  editorRoot?: HTMLElement | null
): () => void {
  if (!anchor) return () => undefined
  // The clicked callout may already be re-rendered (detached) when the save starts, so the host can name the root
  const root = editorRoot ?? anchor.closest<HTMLElement>('.ProseMirror')
  const marked = new Set<HTMLElement>()

  const apply = () => {
    // Callouts the editor has replaced are gone for good: do not keep their nodes alive
    marked.forEach(callout => {
      if (!callout.isConnected) marked.delete(callout)
    })
    const targets = new Set<HTMLElement>([anchor])
    root?.querySelectorAll<HTMLElement>('.variable-wrapper').forEach(wrapper => {
      const placeholder = wrapper.querySelector('.variable-placeholder')
      if (placeholder?.getAttribute('data-variable-name') !== variableName) return
      wrapper.querySelectorAll<HTMLElement>('.assign-callout').forEach(callout => targets.add(callout))
    })
    targets.forEach(callout => {
      if (!callout.classList.contains(CALLOUT_PENDING_CLASS) || !callout.querySelector(`.${CALLOUT_SPINNER_CLASS}`)) {
        markCalloutPending(callout, true)
      }
      marked.add(callout)
    })
  }

  // childList only: marking adds a class and one child, which settles on the second pass
  const observer = root ? new MutationObserver(apply) : null
  if (root) observer?.observe(root, { childList: true, subtree: true })
  apply()

  return () => {
    observer?.disconnect()
    marked.forEach(callout => markCalloutPending(callout, false))
    marked.clear()
  }
}
